import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { WatchSource } from 'vue'
import Tooltip from '@app/setup/tooltip/uikit-tooltip'
import { TRUNCATION_TOOLTIP_SHOW_DELAY_MS } from '@app/shared/types/tooltip'

const REDACTED_CLASS = 'elements-should-be-redacted'

/**
 * A Vue composable that shows a tooltip for each item in a `v-for` list, but
 * only once its element is actually clipped, using a single shared
 * ResizeObserver. Bind `setItemRef` as the `:ref` callback on each item's
 * element; pass a watch source (e.g. the items' labels) that should trigger
 * re-checking truncation when it changes.
 * @param watchSource - Reactive source to re-check truncation on change
 * @returns `setItemRef`, to bind as the `:ref` callback for each list item
 */
export default function useListTruncationTooltips(
  watchSource: WatchSource<unknown>
): {
  setItemRef: (el: unknown, index: number) => void
} {
  const itemRefs = ref<(HTMLElement | null)[]>([])
  const tooltips = new Map<HTMLElement, { tooltip: Tooltip; title: string }>()
  const observedElements = new Set<HTMLElement>()
  let resizeObserver: ResizeObserver | null = null

  const setItemRef = (el: unknown, index: number): void => {
    itemRefs.value[index] = el instanceof HTMLElement ? el : null
  }

  const createTooltip = (element: HTMLElement, title: string): void => {
    tooltips.set(element, {
      title,
      tooltip: new Tooltip(element, {
        title,
        placement: 'bottom',
        size: 'sm',
        delay: { show: TRUNCATION_TOOLTIP_SHOW_DELAY_MS, hide: 0 },
      }),
    })
  }

  const refreshTooltip = (element: HTMLElement): void => {
    const text = (element.textContent ?? '').trim()
    const isTruncated = element.scrollWidth > element.offsetWidth
    const skip = !text || !!element.closest(`.${REDACTED_CLASS}`)
    const existing = tooltips.get(element)

    if (isTruncated && !skip && !existing) {
      createTooltip(element, text)
    } else if (isTruncated && !skip && existing && existing.title !== text) {
      existing.tooltip.dispose()
      createTooltip(element, text)
    } else if ((!isTruncated || skip) && existing) {
      existing.tooltip.dispose()
      tooltips.delete(element)
    }
  }

  const observeItems = (): void => {
    const currentElements = new Set(
      itemRefs.value.filter(
        (element): element is HTMLElement => element !== null
      )
    )

    observedElements.forEach((element) => {
      if (currentElements.has(element)) return

      resizeObserver?.unobserve(element)
      observedElements.delete(element)

      const existing = tooltips.get(element)
      if (existing) {
        existing.tooltip.dispose()
        tooltips.delete(element)
      }
    })

    currentElements.forEach((element) => {
      observedElements.add(element)
      resizeObserver?.observe(element)
      refreshTooltip(element)
    })
  }

  onMounted(() => {
    resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => refreshTooltip(entry.target as HTMLElement))
    })
    void nextTick(observeItems)
  })

  watch(watchSource, () => nextTick(observeItems))

  onBeforeUnmount(() => {
    resizeObserver?.disconnect()
    tooltips.forEach(({ tooltip }) => tooltip.dispose())
    tooltips.clear()
    observedElements.clear()
  })

  return { setItemRef }
}
